import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { ArrowsLeftRight, Lock, ArrowsClockwise } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import StockList from "./StockList";

/**
 * EMA 10 / 20 crossover scanner — fresh golden / death crosses on
 * NIFTY 500 in the last 3 trading days. Long/short toggle.
 *
 * Refreshed twice daily by the scheduler. Free users see 2-row preview
 * per direction; premium gets the full list.
 */
export default function EmaCrossoverView({ onWatch, watchedSet, onOpenStock }) {
  const [direction, setDirection] = useState("long");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(async (dir) => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get(`/ema-crossover?direction=${dir}`);
      setData(data);
    } catch (e) {
      console.error("/ema-crossover", e);
      setErr(e?.response?.data?.detail || "Failed to load EMA crossovers");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(direction); }, [load, direction]);

  const items = data?.items || [];
  const isPremium = data?.is_premium_view;

  return (
    <div data-testid="ema-crossover-view">
      <div className="mb-4 flex items-start justify-between flex-wrap gap-3 border-b border-subtle pb-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ArrowsLeftRight size={14} weight="bold" className="text-pos" />
            <div className="font-mono text-xs uppercase tracking-[0.25em] text-primary-fg">
              /// EMA 10 / 20 Crossover Breakouts
            </div>
          </div>
          <p className="text-xs text-muted-fg leading-relaxed max-w-2xl">
            Fresh EMA-10 crossing EMA-20 in last 3 trading days · price stacked
            in cross direction · volume ≥ 1.5× 20-day average · liquidity ≥ ₹5 Cr.
            NIFTY 500 universe · refreshed twice daily.
          </p>
        </div>
        <button
          type="button"
          onClick={() => load(direction)}
          disabled={loading}
          data-testid="ema-crossover-reload"
          className="px-3 py-1.5 border border-subtle rounded-sm text-secondary-fg hover:bg-surface-hover text-[10px] font-mono uppercase tracking-widest flex items-center gap-1.5 disabled:opacity-50"
        >
          <ArrowsClockwise size={12} className={loading ? "animate-spin" : ""} weight="bold" />
          Refresh
        </button>
      </div>

      {/* Direction toggle */}
      <div className="mb-4 inline-flex border border-subtle rounded-sm overflow-hidden">
        {[
          { k: "long",  label: "Bullish (10 ↗ 20)", color: "pos" },
          { k: "short", label: "Bearish (10 ↘ 20)", color: "neg" },
        ].map((t) => {
          const active = direction === t.k;
          return (
            <button
              key={t.k}
              type="button"
              onClick={() => setDirection(t.k)}
              data-testid={`ema-direction-${t.k}`}
              className={
                "px-4 py-1.5 text-[11px] font-mono uppercase tracking-widest transition " +
                (active
                  ? (t.color === "pos" ? "bg-pos text-bg" : "bg-neg text-bg")
                  : "text-secondary-fg hover:bg-surface-hover")
              }
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {err && (
        <div className="p-3 mb-3 border border-neg-soft bg-neg-soft text-neg text-sm rounded-sm" data-testid="ema-error">
          {err}
        </div>
      )}

      <StockList
        items={items}
        loading={loading}
        onOpen={(it) => onOpenStock?.({ ...it, ema_cross_mode: true })}
        onWatch={onWatch}
        watchedSet={watchedSet}
        testIdPrefix="ema-row"
        testid="ema-list"
        emptyMessage={`No fresh ${direction === "long" ? "bullish" : "bearish"} EMA crossovers right now. Scanner refreshes twice daily during market hours.`}
        rowProps={(it) => ({
          typeBadge: {
            label: it.crossover_days_ago === 0 ? "TODAY" : `${it.crossover_days_ago}D AGO`,
            tone: it.crossover_days_ago === 0 ? "pos" : "mod",
          },
          rightHint: `${it.ema_gap_pct > 0 ? "+" : ""}${it.ema_gap_pct}% gap · ${it.volume_multiple}× vol`,
        })}
      />

      {!isPremium && data && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mt-6 border border-pos/40 rounded-sm p-5 bg-pos-soft/40 flex items-center justify-between gap-4 flex-wrap"
          data-testid="ema-upgrade"
        >
          <div className="flex items-center gap-3">
            <Lock size={20} className="text-pos shrink-0" />
            <div>
              <div className="font-medium text-primary-fg">
                Showing 2 preview · {data.total - items.length} more locked
              </div>
              <div className="text-sm text-secondary-fg">
                Unlock all EMA-10/20 crossover picks (bullish + bearish) with a ₹5 trial.
              </div>
            </div>
          </div>
          <Link
            to="/pricing"
            className="px-5 py-2.5 rounded-sm text-sm font-medium"
            style={{ background: "hsl(var(--pos))", color: "hsl(var(--bg))" }}
          >
            Start ₹5 trial →
          </Link>
        </motion.div>
      )}
    </div>
  );
}
